<template>
  <v-dialog v-model="dialog" persistent max-width="550px">
    <v-stepper v-model="step" ref="stepper">
      <v-stepper-header v-if="buyConditions">
        <v-stepper-step :complete="step > 1" step="1">{{
          $t("Licences")
        }}</v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step :complete="step > 2" step="2">{{
          $t("Purchase and billing conditions")
        }}</v-stepper-step>
      </v-stepper-header>
      <v-stepper-items>
        <v-stepper-content step="1">
          <v-card>
            <v-card-title>
              <span class="headline">{{ formTitle }}</span>
            </v-card-title>
            <v-card-subtitle>{{ $t("* Required fields") }}</v-card-subtitle>
            <v-card-text>
              <validation-observer ref="form">
                <v-container>
                  <validation-provider
                    v-if="newLicence"
                    v-slot="{ errors }"
                    name="application_id"
                    rules="required"
                  >
                    <v-select
                      data-cy="select-input-application"
                      :label="`${$t('Application')} *`"
                      :items="applications"
                      item-text="name"
                      item-value="id"
                      v-model="editedItem.application_id"
                      :error-messages="errors"
                    ></v-select>
                  </validation-provider>
                  <validation-provider
                    v-if="newLicence"
                    v-slot="{ errors }"
                    name="plan"
                    rules="required_price_plan"
                  >
                    <v-select
                      data-cy="select-input-price"
                      :label="`${$t('Pricing plan')} *`"
                      :items="prices"
                      v-model="editedItem.price"
                      :error-messages="errors"
                      return-object
                      :item-text="(item) => `${item.type}-${item.plan}`"
                    >
                      <template v-slot:item="{ item }">
                        {{ formatPlan(item) }}
                        <v-spacer></v-spacer>
                        {{ toPrice(item.amount).toFormat() }}
                      </template>
                      <template v-slot:selection="{ item }">
                        {{ formatPlan(item) }}
                        <v-spacer></v-spacer>
                        {{ toPrice(item.amount).toFormat() }}
                      </template>
                    </v-select>
                  </validation-provider>
                  <validation-provider
                    v-slot="{ errors }"
                    name="reseller_entity_id"
                    rules="required"
                  >
                    <v-select
                      data-cy="select-input-entity"
                      :label="`${$t('Entity')} *`"
                      :items="entities"
                      item-text="name"
                      item-value="id"
                      v-model="editedItem.reseller_entity_id"
                      :error-messages="errors"
                    ></v-select>
                  </validation-provider>
                  <validation-provider
                    v-if="newLicence"
                    v-slot="{ errors }"
                    name="quantity"
                    rules="required|min_value:1|max_value:10"
                    mode="aggressive"
                  >
                    <v-text-field
                      data-cy="text-field-quantity"
                      type="number"
                      :min="1"
                      :max="10"
                      :label="`${$t('Quantity')} *`"
                      v-model="editedItem.quantity"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                  <validation-provider
                    v-slot="{ errors }"
                    name="options"
                    rules=""
                  >
                    <v-select
                      name="options"
                      :label="$t('Options')"
                      :items="optionsToDisplay"
                      multiple
                      item-text="name"
                      return-object
                      v-model="options"
                      :error-messages="errors"
                    >
                      <template v-slot:item="{ item }">
                        {{ item.name }}
                        <v-spacer></v-spacer>
                        {{ toPrice(item.amount).toFormat() }}
                      </template>
                    </v-select>
                  </validation-provider>
                </v-container>
                <total-licence-price
                  :price="price"
                  :options="options"
                  :discount="discount"
                  :nextDiscount="nextDiscount"
                ></total-licence-price>
              </validation-observer>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click.stop="$emit('close')">{{
                $t("Cancel")
              }}</v-btn>
              <v-btn
                v-if="newLicence || options.length > 0"
                data-cy="button-next-licence"
                color="primary"
                :loading="loading"
                text
                @click.stop="checkStep1"
                >{{ $t("Next") }}</v-btn
              >
              <v-btn
                v-else
                data-cy="button-save-edit-licence"
                color="primary"
                :loading="loading"
                text
                @click.stop="save"
                >{{ $t("Save") }}</v-btn
              >
            </v-card-actions>
          </v-card>
          <v-snackbar light v-model="discountSnackbar" centered :timeout="4000">
            <lottie-animation
              :path="`${publicPath}animations/party-popper.json`"
              :autoPlay="true"
              :loop="true"
              :height="150"
              :width="150"
            ></lottie-animation>
            <div class="text-h4 text-center">
              {{
                $t("discount-applied", {
                  percent: discount ? discount.percent : "",
                })
              }}
            </div>
          </v-snackbar>
        </v-stepper-content>
        <v-stepper-content step="2">
          <v-card>
            <v-card-title>
              <span class="headline">{{
                $t("Purchase and billing conditions")
              }}</span>
            </v-card-title>
            <v-card-text>
              {{
                $tc("terms-of-sale", editedItem.quantity, {
                  total,
                })
              }}
              <v-checkbox
                data-cy="checkbox-accept"
                :label="$t('Accept')"
                v-model="termsAccepted"
              ></v-checkbox>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click.stop="step = 1">{{
                $t("Back")
              }}</v-btn>
              <v-btn
                data-cy="button-buy-licence"
                color="primary"
                :loading="loading"
                text
                @click.stop="save"
                :disabled="!termsAccepted"
                >{{ $t("Buy") }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-dialog>
</template>

<script>
import Dinero from "dinero.js";
import snackbar from "@/mixins/snackbar";
import priceMixin from "@/mixins/priceMixin";
import LottieAnimation from "lottie-vuejs/src/LottieAnimation";
import { mapState } from "vuex";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import TotalLicencePrice from "./TotalLicencePrice.vue";

export default {
  name: "AddEditLicenceDialog",
  props: ["value"],
  components: {
    ValidationObserver,
    ValidationProvider,
    LottieAnimation,
    TotalLicencePrice,
  },
  mixins: [snackbar, priceMixin],
  data() {
    return {
      loading: false,
      publicPath: process.env.BASE_URL.substring(1),
      discountSnackbar: false,
      step: 1,
      termsAccepted: false,
    };
  },
  watch: {
    dialog(value) {
      if (!value && this.$refs.form) {
        this.termsAccepted = false;
        this.step = 1;
        this.$refs.form.reset();
      }
    },
    "editedItem.application_id"(value) {
      if (value !== this.applicationId && this.$refs.form) {
        this.editedItem.price = {
          plan: "",
          type: null,
        };
        this.editedItem.options = [];
        this.$refs.form.reset("price");
        this.$refs.form.reset("options");
      }
    },
    discount(value) {
      if (value !== null) {
        this.discountSnackbar = true;
      }
    },
    buyConditions(value) {
      if (value) {
        this.$nextTick(() => {
          this.$refs.stepper.steps[this.step - 1].isActive = true;
        });
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    ...mapState({
      editedId: (state) => state.licences.editedId,
      entities: (state) => state.auth.user.reseller_entities,
      applications: (state) =>
        state.applications.items.filter((x) => x.prices.length > 0),
      application: (state) =>
        state.applications.items.find(
          (x) => x.id === state.licences.editedItem?.application_id
        ),
      applicationId: (state) =>
        state.licences.items.find((x) => x.id === state.licences.editedId)
          ?.application_id,
      discounts: (state) => state.licences.discounts,
      total: (state) => state.licences.total,
    }),
    editedItem: {
      get() {
        return this.$store.state.licences.editedItem;
      },
      set(value) {
        this.$store.commit("licences/setEditedItem", value);
      },
    },
    options: {
      get() {
        return this.$store.state.licences.editedItem.options;
      },
      set(value) {
        this.$store.commit("licences/setOptions", value);
      },
    },
    newLicence() {
      return this.editedId === -1;
    },
    buyConditions() {
      return this.newLicence || this.options.length > 0;
    },
    formTitle() {
      return this.editedId === -1
        ? this.$t("New licence")
        : this.$t("Modify licence");
    },
    prices() {
      return this.application?.prices.filter((p) => p.option === false) ?? [];
    },
    optionsToDisplay() {
      return (
        this.application?.prices.filter(
          (p) =>
            p.option === true &&
            this.editedItem.initialOptions.findIndex(
              (o) => o.type === p.type
            ) === -1
        ) ?? []
      );
    },
    price() {
      let price = this.application?.prices.find(
        (x) =>
          x.plan === this.editedItem.price.plan &&
          x.type === this.editedItem.price.type
      );

      return price;
    },
    discountsForPrice() {
      return this.discounts
        .filter((x) => x.price_id === this.price?.id)
        .sort((a, b) => a.quantity > b.quantity);
    },
    discount() {
      let discount = null;
      let discountsFilterByQuantity = this.discountsForPrice.filter(
        (x) => x.quantity <= this.editedItem.quantity
      );
      if (discountsFilterByQuantity.length > 0) {
        discount =
          discountsFilterByQuantity[discountsFilterByQuantity.length - 1];
      }

      return discount;
    },
    nextDiscount() {
      let nextDiscount = null;
      let discountsFilterByQuantity = this.discountsForPrice.filter(
        (x) => this.editedItem.quantity < x.quantity
      );
      if (discountsFilterByQuantity.length > 0) {
        nextDiscount = discountsFilterByQuantity[0];
      }

      return nextDiscount;
    },
  },
  created() {
    if (this.discounts.length === 0) {
      this.$store.dispatch("licences/fetchDiscounts");
    }
  },
  methods: {
    toPrice(value) {
      return Dinero({ amount: value });
    },
    checkStep1() {
      this.$refs.form.validate().then((success) => {
        if (!success) {
          return;
        }
        this.step = 2;
      });
    },
    save() {
      this.$refs.form.validate().then((success) => {
        if (!success) {
          return;
        }
        this.loading = true;
        if (this.editedId === -1) {
          if (this.discount) {
            this.$store.commit("licences/setDiscountId", this.discount.id);
          }
          this.$store
            .dispatch("licences/saveEditedItem")
            .then(() => {
              this.$refs.form.reset();
              this.loading = false;
              this.dialog = false;
              this.snackbar(
                "success",
                this.$t("snackbar-message-licence.added")
              );
            })
            .catch(this.responseError);
        } else {
          this.$store
            .dispatch("licences/updateEditedItem")
            .then(() => {
              this.$refs.form.reset();
              this.loading = false;
              this.dialog = false;
              this.snackbar(
                "success",
                this.$t("snackbar-message-licence.updated")
              );
            })
            .catch(this.responseError);
        }
      });
    },
    responseError(error) {
      this.loading = false;
      if (error.response && error.response.status === 422) {
        let errors = error.response.data.errors;
        this.$refs.form.setErrors(errors);
      } else {
        this.snackbar("error", this.$t("snackbar-message-licence.error"));
      }
    },
  },
};
</script>
